import { aliasesForDelimiters } from "../constants/aliasesForDelimiters";
import { propertyConfig } from "../constants/ruleProperties";
import { isEnglishWordOperator } from "./isEnglishWord";
import { isSpaceSurrounded } from "./isSpaceSurrounded";

export interface RuleRow {
  delimiter: string;
  alias: string;
  property: string;
  operator: string;
  value: string;
}

export const shouldTransposeRow = (row: RuleRow): boolean => {
  const specialProperties = ["installed", "visible installed"];
  return specialProperties.includes(row.property) && isEnglishWordOperator(row.operator);
};

interface AliasDelimiterPair {
  alias: string;
  delimiter: string;
}

const allAliases: AliasDelimiterPair[] = [];

for (const delimiter of Object.keys(aliasesForDelimiters)) {
  const { aliases } = aliasesForDelimiters[delimiter];
  for (const alias of aliases) {
    allAliases.push({ alias, delimiter });
  }
}

allAliases.sort((a, b) => b.alias.length - a.alias.length);

export interface MatchResult {
  alias: string;
  delimiter: string;
  startIndex: number;
  length: number;
}

// List of aliases that must be validated at position 0
const MUST_BE_AT_START = ["is", "is for any", "="];

function isInsideParentheses(text: string, index: number): boolean {
  let open = 0;
  for (let i = 0; i < index; i++) {
    if (text[i] === "(") open++;
    if (text[i] === ")") open--;
  }
  return open > 0;
}

// Only A-Z, because toLowerCase() is not length preserving -- "İ" lowercases to two
// code units -- and the match indices are used against the original text. Every
// alias is ASCII, so folding the rest would not find anything extra anyway.
const foldAscii = (text: string) => text.replace(/[A-Z]/g, (char) => char.toLowerCase());

// AB#3431. Aliases are matched the way the bot matches them: case-insensitively
// (RegexOptions.IgnoreCase on every regex in aiden.Core/ConditionUtil.cs) and only
// when spaces surround them. Every alias in aliasesForDelimiters is lowercase, so
// the search runs against a folded copy and the canonical spelling is what ends up
// on the row -- which is also how "wheN" gets corrected to "when".
function findAllAliases(text: string, aliases: AliasDelimiterPair[]): MatchResult[] {
  let searchable = foldAscii(text);
  const matches: MatchResult[] = [];

  for (const { alias, delimiter } of aliases) {
    let fromIndex = 0;
    while (true) {
      const idx = searchable.indexOf(alias, fromIndex);
      if (idx === -1) break;

      if (isInsideParentheses(text, idx)) {
        fromIndex = idx + alias.length;
        continue;
      }

      if (MUST_BE_AT_START.includes(alias)) {
        if (idx !== 0) {
          fromIndex = idx + alias.length;
          continue;
        }
      }

      if (!isSpaceSurrounded(searchable, idx, alias)) {
        fromIndex = idx + alias.length;
        continue;
      }

      matches.push({
        alias,
        delimiter,
        startIndex: idx,
        length: alias.length,
      });

      // Blank the match out so a shorter alias cannot match inside it. The filler
      // is not whitespace, which also stops the neighbouring text from reading as
      // space-surrounded.
      const fill = "X".repeat(alias.length);
      searchable = searchable.slice(0, idx) + fill + searchable.slice(idx + alias.length);

      fromIndex = idx + alias.length;
    }
  }

  matches.sort((a, b) => a.startIndex - b.startIndex);
  return matches;
}

/*******************************************************
 * 5) Logic to separate property, operator, and value
 *******************************************************/

const allPropertyKeys = Object.keys(propertyConfig).sort((a, b) => b.length - a.length);
const TRANSPOSED_PROPERTIES = ["installed", "visibly installed"];

function parseValueIntoPropertyOperatorValue(chunkText: string): {
  property: string;
  operator: string;
  remainder: string;
} {
  let property = "";
  let operator = "";
  let remainder = chunkText.trim();

  const allOperators = ["is", "is not"];

  for (const propKey of allPropertyKeys) {
    if (remainder.toLowerCase().startsWith(propKey.toLowerCase())) {
      const afterIndex = propKey.length;
      const nextChar = remainder[afterIndex] || ""; // Character after the property
      const validOps = propertyConfig[propKey].operators || [];
      const sortedOps = [...validOps].sort((a, b) => b.length - a.length);

      let isFollowedByOperator = false;
      for (const op of sortedOps) {
        if (remainder.toLowerCase().slice(afterIndex).startsWith(op.toLowerCase())) {
          isFollowedByOperator = true;
          break;
        }
      }

      if (nextChar === "" || nextChar === " " || isFollowedByOperator) {
        property = propKey;
        remainder = remainder.slice(propKey.length).trim();

        // Search for the operator
        if (validOps.length > 0) {
          for (const op of sortedOps) {
            if (remainder.toLowerCase().startsWith(op.toLowerCase())) {
              operator = op;
              remainder = remainder.slice(op.length).trim();
              break;
            }
          }
        }
        break;
      }
    }

    if (!property) {
      for (const propKey of TRANSPOSED_PROPERTIES) {
        // Case 2: Property at the end (transposedValues)
        if (remainder.toLowerCase().endsWith(propKey.toLowerCase())) {
          const propIndex = remainder.toLowerCase().lastIndexOf(propKey.toLowerCase());
          property = propKey;

          let prePropertyText = remainder.slice(0, propIndex).trim();
          for (const op of allOperators) {
            if (prePropertyText.toLowerCase().endsWith(op.toLowerCase())) {
              const opIndex = prePropertyText.toLowerCase().lastIndexOf(op.toLowerCase());
              operator = op;
              remainder = prePropertyText.slice(0, opIndex).trim();
              break;
            }
          }

          if (!operator && propertyConfig[property]?.operators) {
            const validOps = propertyConfig[property].operators.sort((a, b) => b.length - a.length);
            for (const op of validOps) {
              if (prePropertyText.toLowerCase().endsWith(op.toLowerCase())) {
                const opIndex = prePropertyText.toLowerCase().lastIndexOf(op.toLowerCase());
                operator = op;
                remainder = prePropertyText.slice(0, opIndex).trim();
                break;
              }
            }
          }

          remainder = remainder.trim();
          break;
        }
      }
    }
  }

  if (!property) {
    remainder = chunkText.trim();
  }

  return { property, operator, remainder };
}

interface FinalSegment {
  delimiter: string;
  alias: string;
  property: string;
  operator: string;
  value: string;
}

function buildSegmentsFromMatches(text: string, matches: MatchResult[]): FinalSegment[] {
  const segments: FinalSegment[] = [];

  for (let i = 0; i < matches.length; i++) {
    const currentMatch = matches[i];
    const start = currentMatch.startIndex;
    const end = i < matches.length - 1 ? matches[i + 1].startIndex : text.length;

    // Cut by the match length, not by the alias text: the two differ whenever the
    // rule spells the alias with a different case.
    const chunkText = text.substring(start + currentMatch.length, end).trim();

    // Try to parse property/operator
    const { property, operator, remainder } = parseValueIntoPropertyOperatorValue(chunkText);

    segments.push({
      delimiter: currentMatch.delimiter,
      alias: currentMatch.alias,
      property,
      operator,
      value: remainder,
    });
  }

  return segments;
}

export function transformFirstRow(segments: FinalSegment[]): FinalSegment[] {
  if (segments.length === 0) return segments;

  const first = segments[0];

  const splitted1: FinalSegment = {
    delimiter: first.delimiter.trim(),
    alias: first.alias,
    property: "",
    operator: "",
    value: "",
  };

  const splitted2: FinalSegment = {
    delimiter: "",
    alias: "",
    property: first.property,
    operator: first.operator,
    value: first.value,
  };

  const newSegments = [splitted1, splitted2, ...segments.slice(1)];
  return newSegments;
}

export const getSegmentedRule = (rule: string) => {
  const results = findAllAliases(rule, allAliases);
  const segments = buildSegmentsFromMatches(rule, results);
  return segments;
};

// Where the parser would read an operator in this text. Callers that build a rule
// string need it to check that what they wrote parses back into what they meant.
export const findOperatorMatches = (text: string): MatchResult[] => findAllAliases(text, allAliases);

// Splits a full policy line into name + primary delimiter + value + inline comment.
// Primary delimiters (=, is, is for any) come from aliasesForDelimiters.isFirst.
// Requires whitespace (or start/end) around the primary delimiter, which avoids
// matching '=' inside an LDAP DN like OU=X,DC=Y.
//
// `allowEmptyName` lets sections whose name is optional (AD Domain "skip domain
// join", AD OU root) accept lines that start with the primary delimiter and only
// carry an expression on the right.
export const parseLineToRow = (
  content: string,
  options: { allowEmptyName?: boolean } = {}
): {
  name: string;
  delimiter: string;
  value: string;
  inlineComment: string;
} | null => {
  const { allowEmptyName = false } = options;

  const stripped = content.trim().replace(/^;\s*/, "").trim();
  if (!stripped) return null;

  // Peel off the inline comment (first ';' NOT at position 0) so the primary
  // aliases (is, is for any, =) can't accidentally match inside a comment body.
  const semiIdx = stripped.indexOf(";");
  const body = semiIdx >= 0 ? stripped.slice(0, semiIdx).trimEnd() : stripped;
  const inlineComment = semiIdx >= 0 ? stripped.slice(semiIdx + 1).trim() : "";
  if (!body) return null;

  const primaryAliases = Object.entries(aliasesForDelimiters)
    .filter(([, cfg]) => cfg.isFirst)
    .flatMap(([, cfg]) => cfg.aliases)
    .sort((a, b) => b.length - a.length);

  const escapeForRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  for (const alias of primaryAliases) {
    // When the name may be empty the primary alias can also live at the very
    // start of the body (no leading whitespace before it).
    const pattern = allowEmptyName
      ? new RegExp(`(?:^|\\s)${escapeForRegex(alias)}(?:\\s|$)`, "i")
      : new RegExp(`\\s${escapeForRegex(alias)}(?:\\s|$)`, "i");
    const match = body.match(pattern);
    if (!match || match.index === undefined) continue;

    const leadingSpace = match[0].toLowerCase().startsWith(alias.toLowerCase()) ? 0 : 1;
    const aliasStart = match.index + leadingSpace;
    const aliasEnd = aliasStart + alias.length;

    const name = body.slice(0, aliasStart).trim();
    const value = body.slice(aliasEnd).trim();
    if (!value) continue;
    if (!name && !allowEmptyName) continue;

    return { name, delimiter: alias, value, inlineComment };
  }

  return null;
};
